/* eslint-disable react-hooks/refs, react-hooks/immutability --
   This hook's architecture rests on two patterns the lint rules don't
   recognise as legitimate:
   1. Always-fresh ref pattern (paramsRef, onDataRef, etc.) — refs updated
      every render so the stable AG Grid datasource reads latest values from
      its async getRows. See rule 1 in the file header.
   2. Caller-owned grid api ref — the optional `gridRef` param lets pages
      capture AG Grid's api object via `gridRef.current = api` in
      onGridReady. The rule flags "modifying a passed-in value", but this
      is the canonical AG Grid React capture pattern; without it the page
      can't refresh, expand rows, or apply column state from outside the
      hook. */
// frontend/src/hooks/useSsrmGrid.js
//
// AG Grid Server-Side Row Model wiring for every server-paginated grid. The
// hook owns the four steps that must happen together: build the datasource,
// push it to the grid, refresh on filter change, and hand the response's
// chrome data back to the page.
//
// `pageSize` is the one page size of the grid: each request asks the endpoint
// for that many rows, and the returned `pagination` props — spread onto
// <AgGridReact/> — make one cache block one page of that size.
//
// 1. ONE STABLE DATASOURCE. AG Grid keeps a reference to the datasource, so
//    getRows reads `paramsRef.current` at call time: a filter change needs no
//    new datasource and no re-push.
//
// 2. THE HOOK OWNS THE GRID API'S LIFETIME. AG Grid destroys and recreates the
//    grid on some prop identity changes (an inline `getRowId`), and AG Grid
//    React owns the grid's lifetime from its outermost ref. A destroyed api
//    ignores calls (warning #26), so a datasource set on it never reaches the
//    new grid. `onGridReady` sets the api, `onGridPreDestroyed` clears it, and
//    every internal api touch goes through `withApi`. Callers spread BOTH
//    callbacks onto <AgGridReact/>. A tab switch is NOT a recreation: TabsHost keeps a hidden tab's grid mounted
//    (tabs/tabPaneGridLifecycle.test.jsx), so the merchant returns to the same
//    instance, blocks and widths included.
//
// Column sizing is not the hook's: AG Grid sizes the columns of every grid
// (utils/gridDefaults, "Column sizing").
//
// 3. THE HOOK HEARS A DATA CHANGE. The grid's blocks live outside the query
//    cache, so a save that changes what this grid's endpoint reads
//    (`queries/dataChanged`, keyed by the endpoint) reloads them here, with no
//    page code — once the tab is on screen and has no unsaved edits. A save
//    that names this grid in its `edits` reads the rows on screen again by id
//    and leaves each where it stands (`reReadRowsInPlace`): the new sort order
//    shows on the next sort, filter or reload, not under the cursor.

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { sessionApi } from '../services/SessionApiService';
import { affects, gridReads, useOnDataChanged } from '../queries/dataChanged';
import { TabIdContext, useTabActive } from '../tabs/tabsContexts';
import { isDirty as isTabDirty, subscribe as subscribeDirtyTabs } from '../tabs/useDirtyTabs';
import { restoreInitialSort } from '../utils/gridDefaults';
import { ApiError, extractStructuredErrorCode } from '../utils/parseApiError';
import { useChangeEffect } from './useChangeEffect';
import { useGridPagination } from './useGridPagination';


// The group key a grid gives the rows whose group column is NULL — the
// product grids' bucket of items without a Shopify product gid, the
// customer-orders grid's orders without a month. AG Grid cannot open a group
// whose key is null (the leaf request it builds carries no key at all), so
// the bucket is keyed by this string, and the endpoint turns it back into an
// `__isnull=True` filter (api.filters.requested_group).
export const NULL_GROUP_KEY = '__null__';

// Builds the SSRM URL: page + page_size from startRow, ordering from
// sortModel, group_by/group_keys from SSRM grouping, and the user params
// skipping null/empty/'all'.
//
// `figures` carries what the block asks for beyond its rows: the row count
// and a grid's full-dataset totals. They describe the FILTER rather than the
// block, so exactly one block per filter epoch requests them (see
// `epochParams` on the hook) and paging through a large catalog never re-runs
// an aggregate over the whole set. The leaves of an expanded group are the
// one exception: each of their blocks asks for the group's own count.
export function buildSsrmUrl(endpoint, params, gridRequest, pageSize, figures) {
  const search = new URLSearchParams();
  const page = Math.floor((gridRequest?.startRow || 0) / pageSize) + 1;
  search.append('page', String(page));
  search.append('page_size', String(pageSize));

  const sortModel = gridRequest?.sortModel || [];
  if (sortModel.length > 0) {
    const ordering = sortModel
      .map(({ colId, sort }) => (sort === 'desc' ? '-' : '') + colId)
      .join(',');
    search.append('ordering', ordering);
  }

  const rowGroupCols = gridRequest?.rowGroupCols || [];
  if (rowGroupCols.length > 0) {
    search.append('group_by', rowGroupCols[0].id);
    const groupKeys = gridRequest?.groupKeys || [];
    if (groupKeys.length > 0) {
      search.append('group_keys', groupKeys[0]);
    }
  }

  appendUserParams(search, { ...params, ...figures });

  return `${endpoint}?${search.toString()}`;
}


// The user-param half of an SSRM URL — the FILTER, without the paging and
// sorting that describe one block of it.
//
// A bulk action on a grid selection names the same filter the grid listed
// with: the header checkbox means "every row matching this", and the server
// resolves that rule against the query string the write request carries. The
// listing and buildPickerQuery both serialise through here, so the selection
// resolves against the set the merchant was shown.
//
// 'all' is the ItemFilterBar convention for "no filter" on Group / Location
// dropdowns. Skipping it lets callers pass the raw select value without a
// per-page null check.
export function appendUserParams(search, params) {
  for (const [key, value] of Object.entries(params || {})) {
    if (value != null && value !== '' && value !== 'all') {
      search.append(key, String(value));
    }
  }
  return search;
}


// The `?…` a bulk endpoint needs to re-resolve a selection made on a grid
// listing with `params`. Empty string when nothing is filtered, so callers can
// concatenate unconditionally.
export function buildPickerQuery(params) {
  const query = appendUserParams(new URLSearchParams(), params).toString();
  return query ? `?${query}` : '';
}

// The `&…` fragment an export appends (GridExportButton's `getParams`) so the
// file covers the rows a grid listing with `params` shows. Empty string when
// nothing is filtered.
export function buildExportQuery(params) {
  const query = appendUserParams(new URLSearchParams(), params).toString();
  return query ? `&${query}` : '';
}


// The body and structured code of a refused response (`res` may be missing).
async function readRefusal(res) {
  let body = null;
  try { body = res ? await res.json() : null; } catch { /* non-JSON */ }
  return { body, code: extractStructuredErrorCode(body) };
}

// The refusal's own code and body travel with it, so the page's
// `translateRequestError` renders the server's sentence for it (an unknown
// `item_code` filter, say) rather than a generic one.
function refusalError(res, { body, code }) {
  return new ApiError({
    status: res?.status, code, body, message: `HTTP ${res?.status || 'unknown'}`,
  });
}

// Every endpoint behind this hook answers `{results: [...], ...}`: the DRF
// paginators and the reports' `build_report_response`. A 200 without that
// list is a server bug, so it fails the read like any other failed load
// rather than showing an empty grid the merchant would read as "no rows
// match". Checked before `transformRows`, so a transformer reads
// `data.results` as a list.
async function readResults(res) {
  const data = await res.json();
  if (!Array.isArray(data?.results)) {
    throw new Error('response has no results list');
  }
  return data;
}


// The reload a grid owes once one more change lands, the stronger of the two:
// 'purge' (the row ids died) over 'reload' (read the loaded pages again) over
// 'rows' (only values of the rows on screen changed: read those rows again
// and leave them where they stand). `owed` is null when nothing is owed yet.
const OWED_STRENGTH = { rows: 1, reload: 2, purge: 3 };
export function owedRefresh(owed, change) {
  return owed !== null && OWED_STRENGTH[owed] >= OWED_STRENGTH[change] ? owed : change;
}


// The rows the grid shows, read again by id (`?row_ids=`, the backend's
// api.filters.RowIdsFilter) under the grid's filters and written into the
// rows where they stand. Reading the pages again would answer them in the
// new sort order, so a row whose sorted value was just saved would jump away
// from the cursor. One request per `pageSize` ids.
//
// Resolves false when a row did not come back: it has left the rows the
// filters list, and only reading the pages again shows that. Resolves true
// on a grid destroyed mid-read — the recreated grid loads fresh.
export async function reReadRowsInPlace({
  api, endpoint, params, pageSize, buildUrl, transformRows, fetcher, signal,
}) {
  const ids = [];
  // Loading rows have no data yet; group and master-detail rows are not rows
  // of the endpoint's list.
  api.forEachNode((node) => {
    if (node.data && !node.group && !node.detail) ids.push(node.id);
  });
  const getRowId = api.getGridOption('getRowId');
  // A flat first page with no sort: the ids alone decide which rows answer.
  const request = { startRow: 0, sortModel: [], rowGroupCols: [], groupKeys: [] };
  const answered = new Set();
  const updated = [];
  for (let start = 0; start < ids.length; start += pageSize) {
    const batch = ids.slice(start, start + pageSize);
    const url = (buildUrl || buildSsrmUrl)(
      endpoint, params, request, batch.length, { row_ids: batch.join(',') });
    const res = await fetcher(url, signal);
    if (!res || !res.ok) throw refusalError(res, await readRefusal(res));
    const data = await readResults(res);
    if (api.isDestroyed?.()) return true;
    const rows = transformRows ? transformRows(data, request) : data.results;
    rows.forEach((row) => {
      const id = getRowId({ data: row, api });
      answered.add(id);
      const node = api.getRowNode(id);
      if (node) {
        node.updateData(row);
        updated.push(node);
      }
    });
  }
  // As after a reload in place (`storeRefreshed` below): a renderer may show
  // another field of the row than the cell's value.
  if (updated.length > 0) api.refreshCells({ rowNodes: updated, force: true });
  return ids.every((id) => answered.has(id));
}


// Builds the AG Grid datasource. Stable across param changes — reads the
// CURRENT params via paramsRef each time getRows fires. Pure-ish (DI'd
// `fetcher`) so tests can run without React or network.
//
// Stale-sort recovery: backend's SortAliasOrderingFilter raises 400 with
// ``{error: 'invalid_sort', field: <colId>}`` when the request's ordering
// term doesn't resolve through the serializer / annotations / FK
// traversal. This happens after a backend rename when a user has saved
// ui_preferences referencing the old colId. The datasource catches this
// specific 400, puts the page's own order back on the grid
// (utils/gridDefaults restoreInitialSort — AG Grid's state-updated event then
// persists it via useGridState → updatePreference), and triggers a clean
// refetch. From the user's perspective: the page loads in its own order, the
// arrow on it, instead of erroring.
export function createSsrmDatasource({
  endpoint,
  paramsRef,
  pageSize,
  buildUrl,
  transformRows,
  onData,
  onLoadingChange,
  onError,
  fetcher,
  gridRef,
  // Returns the AbortSignal for the CURRENT filter epoch. The hook aborts
  // the signal on every params-change purge, so a slow response fetched
  // under the old filters can never deliver stale rows or chrome state
  // (totals/count via onData) after the new fetch already landed.
  getSignal,
  // The epoch's row count, or null when this epoch has not been told one yet.
  // For a block of the epoch, reading it decides both whether the request
  // asks for the full-dataset figures and whether the block reports a row
  // count at all; writing it is how a response's `count` becomes the epoch's.
  // The leaves of an expanded group neither read nor write it.
  countRef,
  // Extra query params to request alongside the count — the caller's
  // full-dataset figures (Inventory's `totals`). Sent under exactly the same
  // condition, so they arrive together or not at all; one block per epoch
  // carries them. A ref for the same closure-immunity reason as `paramsRef`.
  epochParamsRef,
}) {
  // Whether a block of the current epoch is already asking for the figures.
  // At most one block per epoch asks, whatever `maxConcurrentDatasourceRequests`
  // is: `refresh({purge: false})` reloads every cached block
  // (`LazyStore.refreshStore` → `markNodesForRefresh`), and each concurrent
  // block with a null count would otherwise run the whole-set aggregates.
  //
  // The block that claims the ask does so inside the try below and releases
  // it in the matching `finally`, so no exit path — response, HTTP error,
  // thrown callback, or an epoch change aborting the fetch — can strand it.
  let askingForFigures = false;
  return {
    getRows: async (gridParams) => {
      // The leaf rows of an expanded group are a dataset of their own, not a
      // block of the epoch: their count is the group's, never the epoch's,
      // and the epoch's figures are not theirs to ask for or to overwrite.
      // A group's leaves are few, so each leaf block asks for its own count.
      const groupLeaves = (gridParams.request?.groupKeys?.length ?? 0) > 0;
      // Ask for the epoch figures while this epoch has no count and no other
      // block is already asking. Self-healing on both halves: a block that
      // failed, aborted or skipped the ask leaves the count null, so the next
      // block asks again rather than leaving the grid without one.
      const asking = !groupLeaves && countRef.current == null && !askingForFigures;
      // Inside the try from here on, so the `finally` releases the ask and the
      // block is answered even when the caller-supplied `buildUrl` or
      // `onLoadingChange` throws.
      try {
        if (asking) askingForFigures = true;
        const url = (buildUrl || buildSsrmUrl)(
          endpoint,
          paramsRef.current,
          gridParams.request,
          pageSize,
          groupLeaves ? { count: 1 }
            : asking ? { count: 1, ...epochParamsRef.current }
              : undefined,
        );
        onLoadingChange?.(true);
        const res = await fetcher(url, getSignal?.());
        if (!res || !res.ok) {
          // Try to surface invalid_sort BEFORE bailing — recoverable.
          const refusal = await readRefusal(res);
          const { body, code } = refusal;
          if (res?.status === 400 && code === 'invalid_sort') {
            const staleField = body?.field ?? body?.detail?.field ?? '(unknown)';
            console.warn(
              "useSsrmGrid(%s): replacing stale saved sort %s with the page's own order — backend rejected with invalid_sort",
              endpoint, staleField,
            );
            // Acknowledge the pending block so AG Grid doesn't park it
            // in an error state, then clear the offending sort.
            gridParams.success({ rowData: [], rowCount: 0 });
            const api = gridRef?.current;
            // Skip recovery on a grid destroyed mid-fetch: the recreated
            // grid sends its own request and recovers from its own 400.
            if (api && !api.isDestroyed?.()) {
              // The page's own order goes back on, so the header keeps the
              // arrow for the order the rows come in. AG Grid fires
              // onStateUpdated with source: 'sort', which useGridState
              // persists back to ui_preferences.
              restoreInitialSort(api);
              if (api.getColumnState().some(({ colId, sort }) => colId === staleField && sort)) {
                // The page's own initialSort is a column the endpoint cannot
                // sort by: a bug in the page, not a stale preference.
                onError?.(refusalError(res, refusal));
                return;
              }
              // Trigger a fresh fetch with the page's own order.
              api.refreshServerSide({ purge: true });
            }
            return;
          }
          console.error('useSsrmGrid(%s): HTTP %s', endpoint, res?.status);
          onError?.(refusalError(res, refusal));
          gridParams.fail();
          return;
        }
        const data = await readResults(res);
        // Pages that need to merge side-channel data (e.g. ForecastGrid
        // splicing sales_history columns into each row) override via
        // `transformRows(data, request)`.
        const rows = transformRows
          ? transformRows(data, gridParams.request)
          : data.results;
        // A response that carries a count always wins — endpoints that count
        // unconditionally keep refreshing it per block, and the epoch value is
        // what serves the blocks of an endpoint that counts once (see
        // core.pagination.OptionalCountPagination).
        if (!groupLeaves && data.count != null) countRef.current = data.count;
        // Chrome state (totals, currency, summary, filters, etc.) goes back
        // to the caller via onData — this is the only writeback channel.
        // The second arg surfaces gridParams.request so callers that key off
        // SSRM request shape (e.g. auto-sizing on group-leaf load) can do so
        // without owning their own datasource.
        onData?.(data, gridParams.request);
        onError?.(null);
        // A block with no count for its dataset — it did not ask, or it asked
        // and the endpoint sent none — reports NO rowCount. AG Grid then keeps
        // the count it already holds, and where it holds none it derives one:
        // a block SHORTER than the one requested ends the dataset, a full one
        // means at least one more row (`LazyCache.onLoadSuccess`).
        const rowCount = groupLeaves ? data.count : countRef.current;
        gridParams.success(rowCount == null
          ? { rowData: rows }
          : { rowData: rows, rowCount });
      } catch (err) {
        if (err?.name === 'AbortError') {
          // Filter epoch changed mid-flight: the purge already discarded this
          // block and a fresh fetch is underway. Not an error — but the block
          // must still be ANSWERED. AG Grid gives a concurrent-load slot back
          // only inside the success and fail wrappers it passed us
          // (`LazyBlockLoadingService.onLoadComplete`), so an unanswered block
          // holds its slot for the rest of the grid's life. `fail()` reaches
          // the purged cache as a no-op: `LazyCache.onLoadFailed` returns
          // early once the cache is no longer `live`, which the purge made it
          // synchronously, before this rejection lands.
          gridParams.fail();
          return;
        }
        console.error('useSsrmGrid(%s): %s', endpoint, err?.message);
        onError?.(err);
        gridParams.fail();
      } finally {
        if (asking) askingForFigures = false;
        onLoadingChange?.(false);
      }
    },
  };
}


export function useSsrmGrid({
  endpoint,
  params,
  pageSize = 100,
  buildUrl,
  transformRows,
  onData,
  onError,
  // Gates the first fetch on a prerequisite (e.g. `locations.length > 0`), so
  // no fetch goes out with an unresolved filter dropdown.
  enabled = true,
  // Query params for figures that span the whole FILTERED SET rather than the
  // block — Inventory's `totals`. Requested together with the row count by one
  // block per filter epoch. The values reach `onData` only on the block that
  // carried them, so a page holding them in state keeps them across blocks
  // that do not.
  epochParams,
  // Optional caller-owned ref, for pages whose callbacks are declared BEFORE
  // this call and need the api. Stable across renders.
  //
  // Cache options (`maxBlocksInCache`, `blockLoadDebounceMillis`) live on
  // `defaultGridProps` in utils/gridDefaults.js, not here: AG Grid accepts
  // them only at grid construction, as JSX props.
  gridRef: externalGridRef,
  // Optional caller-owned ref, filled with `refresh` below, for action
  // handlers declared ABOVE this call: `refreshRef.current(...)` needs no
  // dependency-array entry. Handlers below the call use the returned `refresh`.
  refreshRef,
}) {
  const internalGridRef = useRef(null);
  // Memoized so the ref object identity is stable across renders (refs are
  // stable but `externalGridRef || internalGridRef` would be re-evaluated
  // each render, breaking the deps array of the useEffect/useCallback below).
  const gridRef = useMemo(
    () => externalGridRef || internalGridRef,
    [externalGridRef],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync refs every render so getRows reads the latest values without React
  // re-creating the datasource (rule 1 in the header).
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const onDataRef = useRef(onData);
  onDataRef.current = onData;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const transformRowsRef = useRef(transformRows);
  transformRowsRef.current = transformRows;
  // Whether a transformer was supplied is a structural flag for the
  // datasource code path. Hoisted so the useMemo dep list stays simple.
  const hasTransform = !!transformRows;

  // The current epoch's row count. Null re-arms the request for it (and for
  // `epochParams`), which is what purgeRefresh does on every filter change.
  const countRef = useRef(null);
  // Same always-fresh-ref treatment as params: the datasource is memoized for
  // closure-immunity, so nothing it reads may be a dependency.
  const epochParamsRef = useRef(epochParams);
  epochParamsRef.current = epochParams;

  const datasource = useMemo(
    () => createSsrmDatasource({
      endpoint,
      paramsRef,
      pageSize,
      buildUrl,
      transformRows: hasTransform
        ? (data, request) => transformRowsRef.current?.(data, request)
        : undefined,
      onData: (data, request) => onDataRef.current?.(data, request),
      onLoadingChange: setLoading,
      // Caller's onError fires for side effects (toast, telemetry); the
      // returned `error` state is updated regardless so banner-style UIs work
      // without subscribing.
      onError: (err) => {
        setError(err);
        if (err) onErrorRef.current?.(err);
      },
      fetcher: (url, signal) => sessionApi.get(url, signal ? { signal } : undefined),
      // Passed in so the datasource can clear stale sort state when the
      // backend rejects an invalid_sort term (post-rename ui_preferences).
      gridRef,
      getSignal: () => epochRef.current.signal,
      countRef,
      epochParamsRef,
    }),
    // Excludes `params`, `onData`, `onError`, `transformRows`: those flow
    // through refs, so the datasource keeps its identity.
    [endpoint, pageSize, buildUrl, hasTransform, gridRef],
  );

  const paramsKey = JSON.stringify(params);

  // One AbortController per filter epoch. All concurrent block fetches of
  // the current epoch share it; a params change aborts them together (see
  // purgeRefresh) so no pre-purge response can land after the purge.
  // Not aborted on effect teardown: an effect teardown is not an epoch
  // boundary.
  const epochRef = useRef(null);
  if (epochRef.current === null) epochRef.current = new AbortController();

  // Guard for every hook-internal api touch (rule 2). Returns undefined when
  // the api is missing or destroyed.
  const withApi = useCallback((fn) => {
    const api = gridRef.current;
    if (!api || api.isDestroyed?.()) return undefined;
    return fn(api);
  }, [gridRef]);

  // Attaching the datasource and refreshing on a filter change are two
  // effects. Attaching is itself a load (tabs/tabPaneGridLifecycle.test.jsx),
  // so an effect that also purged would throw away the in-flight block-0
  // fetch whenever it re-ran.

  // (1) ATTACH — idempotent. setGridOption is a no-op when the datasource
  // identity is unchanged (it's memoized for closure-immunity), so re-running
  // on a parent re-render is harmless. This covers mount, the enabled
  // false→true flip (e.g. GMROI before its month range resolves), and a
  // datasource-identity change. Grid destroy+recreate is covered separately
  // by onGridReady attaching to the fresh api. paramsKey is deliberately NOT
  // a dependency — the datasource reads params via paramsRef at getRows time,
  // so a filter change needs no re-attach.
  useEffect(() => {
    if (!enabled) return;
    withApi((api) => api.setGridOption('serverSideDatasource', datasource));
  }, [enabled, datasource, withApi]);

  // (2) REFRESH — fires only on a genuine filter/sort param change, via
  // useChangeEffect (which compares the value rather than counting effect
  // runs). The initial load is driven by the attach above — AG Grid fetches
  // when a datasource is first set — so the baseline param value here must
  // NOT trigger a redundant first fetch, which is exactly useChangeEffect's
  // no-fire-on-mount contract. While `enabled` is false the key is held at
  // null so the eventual flip to true registers as a change and refreshes
  // once the datasource is attached.
  const purgeRefresh = useCallback(
    () => {
      // New filter epoch: kill in-flight fetches from the old one BEFORE
      // purging, so their late responses can't race the fresh fetch.
      epochRef.current.abort();
      epochRef.current = new AbortController();
      // The count and the full-dataset figures describe the filter that is
      // being replaced, so the new epoch has to be told them again.
      countRef.current = null;
      withApi((api) => api.refreshServerSide({ purge: true }));
    },
    [withApi],
  );
  useChangeEffect(enabled ? paramsKey : null, purgeRefresh);

  const onGridReady = useCallback(
    ({ api }) => {
      gridRef.current = api;
      // A reload in place keeps the row nodes and re-renders a cell only when
      // its VALUE changed (AG Grid change detection), while a renderer may
      // show another field of the row (the recommendations' "Supplier
      // changed" badge reads `is_stale` beside the supplier name). So once
      // the store has reloaded — `storeRefreshed` fires for a reload, not for
      // a purge — every visible cell is refreshed regardless.
      api.addEventListener('storeRefreshed', () => api.refreshCells({ force: true }));
      // Critical for the destroy+recreate cycle. AG Grid fires gridReady for
      // each NEW grid; the old api is dead by then, so attaching the
      // datasource here (and not relying on the params-change effect above
      // to re-fire) is what lets the recreated grid actually load rows.
      if (enabled) {
        api.setGridOption('serverSideDatasource', datasource);
      }
    },
    [gridRef, datasource, enabled],
  );

  // Fires immediately before AG Grid tears the grid down — the tab closed, or
  // a prop changed identity in a way the React wrapper treats as fatal (e.g.
  // inline `getRowId` callbacks). Clearing the api here means the
  // params-change effect above bails on the dead grid and the next
  // onGridReady (for the recreated grid) re-attaches everything.
  const onGridPreDestroyed = useCallback(() => {
    gridRef.current = null;
  }, [gridRef]);

  // A reload the grid owes (see the data-change block below): null, 'rows'
  // (the rows on screen, by id), 'reload' (the loaded blocks, in place) or
  // 'purge' (throw them away). Any refresh pays it, so a page's own refresh
  // after a discard is not followed by a second one.
  const refreshOwedRef = useRef(null);

  // Page-driven refetch: a discard, a filter the page reset.
  //
  //   purge: true  (default) — throw the loaded blocks away and start from the
  //                first page. For a row set that changed shape.
  //   purge: false — refetch the blocks already loaded, in place, keeping the
  //                merchant's scroll position. For rows that are on screen.
  //
  // Pages refresh THROUGH the hook: a bare `api.refreshServerSide()` keeps
  // the epoch figures below that the refetch makes stale.
  const refresh = useCallback(({ purge = true } = {}) => {
    refreshOwedRef.current = null;
    // Both modes drop the epoch figures. `purge: true` changed the row set, so
    // the count is stale; `purge: false` re-reads rows after a change, which
    // is exactly when a full-dataset total (Inventory's on-hand and stock
    // value) stops matching the rows — the cheaper refresh must not keep the
    // more expensive number frozen.
    countRef.current = null;
    withApi((api) => api.refreshServerSide({ purge }));
  }, [withApi]);
  if (refreshRef) refreshRef.current = refresh;

  // A save that edited the rows of this grid in place: read the rows on
  // screen again by id and leave each where it stands. A row that did not come
  // back has left the filtered set, which only a reload of the pages shows.
  // Group header rows sum their rows, so they are read again too: a reload
  // without a route reads only the top level (AG Grid
  // server-side-model-updating-refresh). The epoch figures (Inventory's
  // totals) span the whole filtered set, so they are read again on their own.
  // Every grid touch after a read goes through `withApi`: the grid may have
  // been destroyed while the read was in flight.
  const reReadRows = useCallback(async () => {
    refreshOwedRef.current = null;
    const api = gridRef.current;
    if (!api || api.isDestroyed?.()) return;
    const signal = epochRef.current.signal;
    const fetcher = (url) => sessionApi.get(url, { signal });
    try {
      const complete = await reReadRowsInPlace({
        api,
        endpoint,
        params: paramsRef.current,
        pageSize,
        buildUrl,
        transformRows: hasTransform
          ? (data, request) => transformRowsRef.current?.(data, request)
          : undefined,
        fetcher,
        signal,
      });
      if (!complete) {
        refresh({ purge: false });
        return;
      }
      withApi((live) => {
        if (live.getRowGroupColumns().length > 0) live.refreshServerSide({ purge: false });
      });
      if (epochParamsRef.current) {
        const request = { startRow: 0, sortModel: [] };
        const url = (buildUrl || buildSsrmUrl)(
          endpoint, paramsRef.current, request, 1, { count: 1, ...epochParamsRef.current });
        const res = await fetcher(url);
        if (!res || !res.ok) throw refusalError(res, await readRefusal(res));
        const data = await readResults(res);
        countRef.current = data.count;
        onDataRef.current?.(data, request);
      }
      setError(null);
    } catch (err) {
      // A filter change mid-read: the purge reloads every row anyway.
      if (err?.name === 'AbortError') return;
      console.error('useSsrmGrid(%s): %s', endpoint, err?.message);
      setError(err);
      onErrorRef.current?.(err);
    }
  }, [gridRef, endpoint, pageSize, buildUrl, hasTransform, refresh, withApi]);

  // A save changed what this grid reads (`queries/dataChanged`): read the rows
  // on screen again when the save names this grid in its `edits`, reload the
  // blocks on screen in place otherwise, or throw them away when the change
  // killed the row ids (`purgeOn`). The reload is owed instead, and runs later, in two
  // cases: a tab with unsaved edits (it would write the server's values over
  // the edited cells while the edits stay queued) runs it once the tab has
  // none left (`tabs/useDirtyTabs`); a hidden tab (a tab stays mounted once
  // shown, tabs/TabPane.jsx) runs it when the merchant comes back, so one
  // save does not reload ten grids nobody is looking at. A grid not enabled
  // yet loads fresh when it is.
  const { reads, purgeOn } = gridReads(endpoint);
  const tabId = useContext(TabIdContext);
  const tabActive = useTabActive();
  const tabActiveRef = useRef(tabActive);
  tabActiveRef.current = tabActive;
  const settleOwedRefreshRef = useRef(null);
  settleOwedRefreshRef.current = () => {
    const owed = refreshOwedRef.current;
    if (owed === null || !tabActiveRef.current || isTabDirty(tabId)) return;
    if (owed === 'rows') reReadRows();
    else refresh({ purge: owed === 'purge' });
  };
  useOnDataChanged(reads, (kinds, edits) => {
    if (!enabled) return;
    // `everything` reaches every reader, but kills ids only where some kind does.
    const change = purgeOn.length > 0 && affects(kinds, purgeOn) ? 'purge'
      : edits.includes(endpoint) ? 'rows'
        : 'reload';
    refreshOwedRef.current = owedRefresh(refreshOwedRef.current, change);
    settleOwedRefreshRef.current();
  });
  useEffect(() => subscribeDirtyTabs(() => settleOwedRefreshRef.current()), []);
  useEffect(() => {
    if (tabActive) settleOwedRefreshRef.current();
  }, [tabActive]);

  const pagePagination = useGridPagination(pageSize);
  const pagination = useMemo(
    () => ({ ...pagePagination, cacheBlockSize: pageSize }),
    [pagePagination, pageSize],
  );

  return {
    onGridReady, onGridPreDestroyed,
    gridRef, refresh, datasource, loading, error, pagination,
  };
}
